import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

/**
 * Bullet-list cell — inline bullet list (2-line clamp per item) with a
 * light-card popover on hover. Used across the Diagnosis surfaces
 * (Ongoing table, Past table, Trend audit table) for Description/Evidence
 * and Medicines columns.
 *
 * The popover is PORTALLED to `document.body` with `position: fixed`,
 * so it escapes every ancestor overflow-clip (including the table's
 * horizontal-scroll clip that used to cut off tooltips on the last row).
 * Flips above/below based on viewport space at hover time; horizontal
 * position clamps to keep the popover on-screen.
 *
 * Reveal is gated on truncation — if every visible bullet fits inside
 * its 2-line clamp with no ellipsis, hovering does nothing. Matches
 * MetaTooltip's "only show when there's something to reveal" rule so
 * the two components feel consistent across sections.
 *
 * Parent cell must carry the `dg3-c-bullets` class so the row's grid
 * lays out correctly and the list top-aligns with sibling cells.
 */
const BULLET_POP_MIN_W = 240;
const BULLET_POP_MAX_W = 380;
const BULLET_POP_MAX_H = 260;

const BulletListCell = ({ items }) => {
  const list = (items || []).filter((x) => x && String(x).trim());
  const [open, setOpen] = useState(false);
  const [popStyle, setPopStyle] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const anchorRef = useRef(null);
  const itemRefs = useRef([]);

  // Re-measure after every render so a column resize or an item swap
  // recomputes the truncated flag. Layout-effect so the check runs
  // before paint, no flash of an about-to-be-hidden tooltip. Only need
  // ONE truncated item for the tooltip to be relevant — kick out early.
  useLayoutEffect(() => {
    const cells = itemRefs.current.filter(Boolean);
    if (cells.length === 0) {
      setTruncated(false);
      return;
    }
    // scrollHeight = natural height ignoring the -webkit-box clamp,
    // clientHeight = clipped visible height. When the former exceeds
    // the latter the ellipsis is showing.
    const any = cells.some((el) => el.scrollHeight - el.clientHeight > 1);
    setTruncated(any);
  }, [list]);

  if (list.length === 0) {
    return <span className="dg3-c-bullets-empty">—</span>;
  }

  const show = () => {
    if (!truncated) return;
    const node = anchorRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const estHeight = Math.min(BULLET_POP_MAX_H, 24 + list.length * 22);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < estHeight + 16;
    const top = openAbove ? Math.max(8, rect.top - estHeight - 6) : rect.bottom + 6;
    const width = Math.max(BULLET_POP_MIN_W, Math.min(BULLET_POP_MAX_W, rect.width));
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(maxLeft, Math.max(8, rect.left));
    setPopStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      right: "auto",
      width: `${width}px`,
      maxWidth: `${BULLET_POP_MAX_W}px`,
      maxHeight: `${BULLET_POP_MAX_H}px`,
    });
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <span
      ref={anchorRef}
      className="dg3-bullet-anchor"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <ul className="dg3-bullet-list">
        {list.map((name, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} ref={(el) => { itemRefs.current[i] = el; }}>
            {name}
          </li>
        ))}
      </ul>
      {open && popStyle && ReactDOM.createPortal(
        <div className="dg3-bullet-pop" style={popStyle} role="tooltip">
          <ul>
            {list.map((name, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{name}</li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </span>
  );
};
BulletListCell.propTypes = { items: PropTypes.array };

export default BulletListCell;
